import { type FunctionComponent } from 'react';
import { isDefined } from '../../../../../utils/array/filter.utils';
import { type HomePageImageFieldsFragment } from '../../../../__generated__/graphql-client-types';
import { PLACEHOLDER_OPTIONS } from '../../../../constants/cloudinary/general';
import { type CloudinaryOptions } from '../../../../helpers/cloudinary-helper/cloudinary-helper';
import { resolveHomepageSectionsImage } from '../../../../helpers/construct/page-content.helper';
import type { CarouselItemContent, ImageContent } from '../../../../types/construct.types';
import { Link } from '../../../common-components/link/link.component';
import { CloudinaryImage } from '../../../images/cloudinary-image/cloudinary-image.component';

export type HomeCarouselOptionCardProps = {
	cloudinaryOptions: CloudinaryOptions;
	item: CarouselItemContent;
	imageClassName?: string;
	isSingleDisplay?: boolean;
	itemsCount?: number;
};

export const GenericCarouselCardOption: FunctionComponent<{ title: string }> = ({ title }) => (
	<span
		className="absolute z-1 bottom-0 left-0 right-0 db tc bg-theme-primary-lighter pa2 ttu fw5 f6 br2 br--bottom"
		style={{ letterSpacing: 1 }}>
		{title}
	</span>
);

export type CarouselCardOptionWithSubtitleProps = {
	title: string;
	subtitle?: string | null;
	containerClassName?: string;
	titleClassName?: string;
	subtitleClassName?: string;
};
export const CarouselCardOptionWithSubtitle: FunctionComponent<CarouselCardOptionWithSubtitleProps> = ({
	title,
	subtitle,
	containerClassName = 'flex flex-column items-center justify-between w-auto pa2 ttu fw5 f6 tracked',
	titleClassName = 'tc',
	subtitleClassName = ''
}) => (
	<span className={`${containerClassName}`}>
		<span className={`${titleClassName}`}>{title}</span>
		{subtitle && <span className={`${subtitleClassName}`}>{subtitle}</span>}
	</span>
);
type CarouselItemContentWithTitleAndSubtitle = CarouselItemContent & {
	fields: { image: ImageContent; title: string; subtitle: string };
};

type CarouselItemContentWithTitle = CarouselItemContent & {
	fields: { image: ImageContent; title: string };
};

/**
 * This function sorts the keys of an object alphabetically and joins them into a single string.
 * It is needed to consistently compare the keys of the object regardless of their original order.
 * The localeCompare is needed to ensure the keys are sorted correctly according to the locale, rather than just by Unicode code points.
 */
function sortAndStringifyDefinedKeys(obj: Record<string, unknown>): string {
	return Object.keys(obj)
		.filter((key) => isDefined(obj[key]))
		.map((key) => key.toLowerCase())
		.sort((a, b) => a.localeCompare(b))
		.join();
}

function isCarouselItemContentWithSubtitle(item: CarouselItemContent): item is CarouselItemContentWithTitleAndSubtitle {
	return 'fields' in item && sortAndStringifyDefinedKeys(item.fields) === 'image,link,subtitle,title';
}

function isCarouselItemContentWithTitle(item: CarouselItemContent): item is CarouselItemContentWithTitle {
	return 'fields' in item && item && sortAndStringifyDefinedKeys(item.fields) === 'image,link,title';
}

export const HomeCarouselOptionFactory: FunctionComponent<{ item: CarouselItemContent }> = ({ item }) => {
	return isCarouselItemContentWithSubtitle(item) ? (
		<CarouselCardOptionWithSubtitle
			title={item.fields.title}
			subtitle={item.fields.subtitle}
			containerClassName="flex flex-column items-center justify-between w-auto pa2 ttu fw5 f6 tracked lh-copy"
		/>
	) : isCarouselItemContentWithTitle(item) ? (
		<GenericCarouselCardOption title={item.fields.title} />
	) : (
		<></>
	);
};

export type ExclusiveDealsDisplayImageProps = {
	isSingleMobile: boolean;
	responsiveClassName?: string;
	image: HomePageImageFieldsFragment;
	cloudinaryOptions: CloudinaryOptions;
} & Pick<HomeCarouselOptionCardProps, 'imageClassName'>;

const ExclusiveDealsDisplayImage: FunctionComponent<ExclusiveDealsDisplayImageProps> = ({
	responsiveClassName,
	image,
	cloudinaryOptions,
	imageClassName
}) => (
	<CloudinaryImage
		{...image}
		className={`${imageClassName} ${responsiveClassName} w-100`}
		options={cloudinaryOptions}
		useDimensions={false}
		publicID={image.id}
		placeholderOptions={{
			...cloudinaryOptions,
			...PLACEHOLDER_OPTIONS
		}}
		lowQualityPlaceholder={image.lowQualityPlaceholder}
	/>
);

export const HomeCarouselOptionCard: FunctionComponent<HomeCarouselOptionCardProps> = ({
	item,
	imageClassName = 'br2',
	// Prop used to make adjustments if there is only one exclusive deals slide
	isSingleDisplay,
	cloudinaryOptions
}) => {
	const {
		link: { linkUrl: url, trackingCode: tracking },
		image
	} = item.fields;

	return (
		<Link className="flex flex-column items-center w-100 relative" url={url} analyticsHook={tracking ?? ''} color="grey-darker">
			{isSingleDisplay ? (
				<>
					<ExclusiveDealsDisplayImage
						image={resolveHomepageSectionsImage(image)}
						imageClassName={imageClassName}
						responsiveClassName="db dn-ns"
						isSingleMobile={true}
						cloudinaryOptions={cloudinaryOptions}
					/>
					<ExclusiveDealsDisplayImage
						image={resolveHomepageSectionsImage(image)}
						imageClassName={imageClassName}
						responsiveClassName="dn db-ns"
						isSingleMobile={false}
						cloudinaryOptions={cloudinaryOptions}
					/>
				</>
			) : (
				<ExclusiveDealsDisplayImage
					image={resolveHomepageSectionsImage(image)}
					imageClassName={imageClassName}
					isSingleMobile={false}
					cloudinaryOptions={cloudinaryOptions}
				/>
			)}

			<HomeCarouselOptionFactory item={item} />
		</Link>
	);
};
